import React, { useState } from "react"
import { Source } from "../../types/route"
import { useSources } from "../../hooks/useSources"
import { FaSearch, FaBuilding, FaSpinner, FaTimes, FaExclamationCircle } from "react-icons/fa"
import { UseFormRegister, FieldError, UseFormSetValue, UseFormWatch } from "react-hook-form"
import { RouteFormData } from "../../types/route"

interface SourceSelectorProps {
  register: UseFormRegister<RouteFormData>
  setValue: UseFormSetValue<RouteFormData>
  watch: UseFormWatch<RouteFormData>
  error?: FieldError
  label: string
  fieldPath: "source.data" | "target.data"
}

const SourceSelector: React.FC<SourceSelectorProps> = ({ register, setValue, watch, error, label, fieldPath }) => {
  const { sources, loading, searchSources } = useSources()
  const [searchQuery, setSearchQuery] = useState("")
  const [showDropdown, setShowDropdown] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)

  const currentValue = watch(fieldPath) as Source

  const handleSearch = (query: string) => {
    setSearchQuery(query)
    if (query.length > 2) {
      setHasSearched(true)
      searchSources(query)
      setShowDropdown(true)
    } else {
      setShowDropdown(false)
      setHasSearched(false)
    }
  }

  const handleSelect = (source: Source) => {
    setValue(`${fieldPath}.id`, source.id, { shouldValidate: true })
    setValue(`${fieldPath}.name`, source.name, { shouldValidate: true })
    setValue(`${fieldPath}.alias`, source.aliases ? source?.aliases[0] : "", { shouldValidate: true })
    setShowDropdown(false)
    setSearchQuery("")
    setHasSearched(false)
  }

  const handleClear = () => {
    setValue(`${fieldPath}.id`, "", { shouldValidate: true })
    setValue(`${fieldPath}.name`, "", { shouldValidate: true })
    setValue(`${fieldPath}.alias`, "", { shouldValidate: true })
  }

  const hasSelection = currentValue?.id || currentValue?.name
  const showNoResults = hasSearched && !loading && sources.length === 0 && searchQuery.length > 2

  return (
    <div className="space-y-4">
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Buscar {label} <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            placeholder={`Buscar ${label.toLowerCase()}...`}
            className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
              error ? "border-red-300 focus:ring-red-500" : "border-gray-300"
            }`}
          />
          {loading && (
            <FaSpinner className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 animate-spin" />
          )}
        </div>

        {showDropdown && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            {sources.length > 0 ? (
              sources.map((source) => (
                <button
                  key={source.id}
                  type="button"
                  onClick={() => handleSelect(source)}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center space-x-3 border-b border-gray-100 last:border-b-0"
                >
                  <FaBuilding className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 truncate">{source.name}</div>
                    <div className="text-sm text-gray-500">ID: {source.id}</div>
                    {source.alias && <div className="text-xs text-gray-400">Alias: {source.alias}</div>}
                  </div>
                </button>
              ))
            ) : showNoResults ? (
              <div className="px-4 py-3 text-center text-gray-500">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <FaExclamationCircle className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium">No se encontraron resultados</span>
                </div>
                <p className="text-xs text-gray-400">
                  Ningún source coincide con "{searchQuery}". Intente con otro término de búsqueda.
                </p>
              </div>
            ) : null}
          </div>
        )}
      </div>

      {/* Hidden form fields for react-hook-form */}
      <div className="hidden">
        <input
          {...register(`${fieldPath}.id`, {
            required: "Source ID is required",
          })}
          type="hidden"
        />
        <input
          {...register(`${fieldPath}.name`, {
            required: "Source name is required",
          })}
          type="hidden"
        />
        <input {...register(`${fieldPath}.alias`)} type="hidden" />
      </div>

      {/* Display current selection - Read-only */}
      {hasSelection && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center space-x-2">
              <FaBuilding className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-800">Source seleccionada</span>
            </div>
            <button
              type="button"
              onClick={handleClear}
              className="p-1 text-blue-600 hover:bg-blue-100 rounded transition-colors"
              title="Borrar selección"
            >
              <FaTimes className="w-3 h-3" />
            </button>
          </div>
          <div className="space-y-2">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-blue-700 mb-1">ID</label>
                <div className="text-sm text-blue-900 bg-blue-100 px-3 py-2 rounded border">
                  {currentValue?.id || "No especificado"}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-blue-700 mb-1">Nombre</label>
                <div className="text-sm text-blue-900 bg-blue-100 px-3 py-2 rounded border">
                  {currentValue?.name || "No especificado"}
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-blue-700 mb-1">Alias</label>
                <div className="text-sm text-blue-900 bg-blue-100 px-3 py-2 rounded border">
                  {currentValue?.alias || "No especificado"}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Validation error message */}
      {error && (
        <div className="text-sm text-red-600 flex items-center space-x-1">
          <FaExclamationCircle className="w-3 h-3" />
          <span>{error.message}</span>
        </div>
      )}

      {/* Validation message */}
      {!hasSelection && !error && (
        <div className="text-sm text-gray-500 italic">
          Por favor, busque y seleccione un source del menú desplegable de arriba
        </div>
      )}
    </div>
  )
}

export default SourceSelector
